/* ET - Countdown: live countdown to a wall-clock date/time in an IANA timezone. */
(() => {
  if (customElements.get('et-countdown')) return;

  const pad = (n) => String(n).padStart(2, '0');

  // Offset (ms) between the wall-clock time in `timeZone` and UTC at instant `ts`.
  const zoneOffset = (ts, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(ts));
    const p = {};
    parts.forEach(({ type, value }) => { p[type] = Number(value); });
    return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) - ts;
  };

  // Converts "YYYY-MM-DD" + "HH:MM" in `timeZone` to a UTC timestamp (DST-safe).
  const toTimestamp = (date, time, timeZone) => {
    const [y, m, d] = String(date).split('-').map(Number);
    const [h = 0, min = 0] = String(time || '00:00').split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(h) || Number.isNaN(min)) return NaN;

    const wall = Date.UTC(y, m - 1, d, h, min);
    try {
      const first = zoneOffset(wall, timeZone);
      let ts = wall - first;
      const second = zoneOffset(ts, timeZone);
      if (second !== first) ts = wall - second;
      return ts;
    } catch (e) {
      // Unknown timezone: fall back to the visitor's local time.
      return new Date(y, m - 1, d, h, min).getTime();
    }
  };

  class EtCountdown extends HTMLElement {
    connectedCallback() {
      this.target = toTimestamp(this.dataset.date, this.dataset.time, this.dataset.timezone);
      if (Number.isNaN(this.target)) {
        this.hidden = true;
        return;
      }

      this.values = {};
      this.querySelectorAll('[data-et-countdown-value]').forEach((el) => {
        this.values[el.dataset.etCountdownValue] = el;
      });

      // Optional second target: once the first is reached, count down again to it (e.g. a final day).
      this.restart = this.dataset.restartDate
        ? toTimestamp(this.dataset.restartDate, this.dataset.restartTime, this.dataset.timezone)
        : NaN;

      // Optional start moment: stay hidden until then, then appear on its own.
      const start = this.dataset.startDate
        ? toTimestamp(this.dataset.startDate, this.dataset.startTime, this.dataset.timezone)
        : NaN;
      if (!Number.isNaN(start) && Date.now() < start) {
        this.hidden = true;
        this.waitTimer = window.setInterval(() => {
          if (Date.now() < start) return;
          window.clearInterval(this.waitTimer);
          this.hidden = false;
          this.run();
        }, 1000);
        return;
      }

      this.hidden = false;
      this.run();
    }

    run() {
      this.tick();
      this.classList.add('is-ready');
      if (!this.expired) this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      window.clearInterval(this.waitTimer);
    }

    set(unit, value) {
      const el = this.values[unit];
      if (el) el.textContent = pad(value);
    }

    tick() {
      let diff = this.target - Date.now();
      if (diff <= 0 && this.restart > this.target) {
        this.target = this.restart;
        this.restart = NaN;
        this.classList.add('is-restarted');
        this.dispatchEvent(new CustomEvent('et-countdown:restart', { bubbles: true }));
        diff = this.target - Date.now();
      }

      if (diff <= 0) {
        this.expire();
        return;
      }

      const total = Math.floor(diff / 1000);
      this.set('days', Math.floor(total / 86400));
      this.set('hours', Math.floor((total % 86400) / 3600));
      this.set('minutes', Math.floor((total % 3600) / 60));
      this.set('seconds', total % 60);
    }

    expire() {
      if (this.expired) return;
      this.expired = true;
      window.clearInterval(this.timer);
      ['days', 'hours', 'minutes', 'seconds'].forEach((unit) => this.set(unit, 0));

      const action = this.dataset.expired;
      if (action === 'hide') {
        this.hidden = true;
      } else if (action === 'text') {
        const units = this.querySelector('.et-countdown__units');
        const message = this.querySelector('[data-et-countdown-expired]');
        if (units) units.hidden = true;
        if (message) message.hidden = false;
      }

      this.classList.add('is-expired');
      this.dispatchEvent(new CustomEvent('et-countdown:expired', { bubbles: true }));
    }
  }

  customElements.define('et-countdown', EtCountdown);
})();
